/**
 * English version of the privacy policy. Translation of PrivacyRu.tsx (the
 * Russian original); keep both in step — same sections, same claims.
 */
import { LegalDoc, List, Section } from '../LegalDoc';

const UPDATED = 'August 5, 2026';

export function PrivacyEn() {
  return (
    <LegalDoc
      title="Privacy Policy"
      updated={UPDATED}
      sibling={{ href: '/terms', label: 'Terms of Use' }}
    >
      <p className="mt-4 text-sm text-ink-muted">
        This is a translation of the Russian original. If they differ, the Russian version prevails.
      </p>
      <Section title="In short">
        <p>
          Bunker is the messenger and voice chat for teams on the cispracs platform. We store what a
          messenger cannot work without: who you are and what you sent. We do not sell data, show
          ads, or track you in other apps and on other websites.
        </p>
      </Section>

      <Section title="What data we process">
        <List
          items={[
            ['Account', 'your identifier on cispracs, nickname, display name, avatar, team and its tag — received from the platform when you sign in.'],
            ['Messages and attachments', 'what you write and attach: text, photos, files, voice messages, polls, reactions.'],
            ['Servers and channels', 'membership, roles, permissions, folders and server order, read marks.'],
            ['Voice', 'who is in a channel right now and the microphone state. The audio itself is transmitted in real time and is not recorded.'],
            ['Technical', 'device sessions (for the “Devices” list and signing out), app version, errors and reports sent via “Report a bug”.'],
          ]}
        />
      </Section>

      <Section title="Why">
        <p>
          Only to make the app work: deliver messages, show history and unread messages, connect
          voice, apply permissions, send notifications and handle reports. No profiling and no ads.
        </p>
      </Section>

      <Section title="Who sees it">
        <List
          items={[
            ['Other members', 'a message is seen by those it is addressed to: the members of the channel or the direct chat.'],
            ['Server moderators', 'a report on a message, together with a snapshot of its text, is visible to that server’s moderators.'],
            ['Platform moderation', 'critical reports and bug reports go to cispracs moderators.'],
            ['Hosting and storage', 'the servers and object storage Bunker runs on.'],
          ]}
        />
        <p className="mt-3">
          We do not share data with ad networks or analytics brokers, because we do not use any.
        </p>
      </Section>

      <Section title="How long we keep it">
        <p>
          Messages and attachments — until the author or the server owner deletes them. A deleted
          server stays in the trash for 30 days, then it is erased. Moderation logs are kept so a
          decision can be reviewed. Deleting your account erases your profile and unlinks the
          history from it — see “How to delete your account and data”.
        </p>
      </Section>

      <Section title="Your rights">
        <List
          items={[
            ['Delete your account', 'in the app: profile → “Delete account”. Details below.'],
            ['Delete a message', 'for yourself or for everyone — if you have the permission.'],
            ['Hide a conversation', 'deleting a direct chat hides it for you and does not erase it for the other person.'],
            ['Block and report', 'a user or a message — from the profile card and the message menu.'],
            ['Sign out of a device', 'the session list in your profile settings.'],
          ]}
        />
      </Section>

      <Section id="delete" title="How to delete your account and data">
        <p className="mb-2">
          In the mobile app: the profile icon at the bottom left →{' '}
          <b className="font-medium text-ink">“Delete account”</b>. You confirm once; it cannot be
          undone. The web version has no button yet — write to the address below instead.
        </p>
        <List
          items={[
            ['Deleted immediately', 'profile, name, avatar, bio and status, friends and friend requests, blocks, folders, read marks, notification settings, push subscriptions, “reply later”, membership in all servers.'],
            ['Unlinked', 'the link to your cispracs account. Signing in again creates a new, empty Bunker account — the old one cannot be restored.'],
            ['Ended', 'all sessions on all devices.'],
            ['Kept', 'messages and files you sent — they are part of other people’s conversations, so they are not erased from their chats, but they are shown without your name and avatar. Moderation records are kept so a decision can be reviewed.'],
            ['Your servers', 'a server where you are the only member is deleted with your account. A server with other members must first be transferred to another owner or deleted — otherwise deleting your account would close it for everyone.'],
          ]}
        />
        <p className="mt-3">
          No access to the app? Write from the email linked to your cispracs account to{' '}
          <a className="text-accent underline" href="mailto:privacy@cispracs.ru">
            privacy@cispracs.ru
          </a>{' '}
          — we will delete your account and data on request.
        </p>
      </Section>

      <Section title="Children">
        <p>
          Bunker is not intended for children under 13 and does not knowingly collect their data.
        </p>
      </Section>

      <Section title="Phone permissions">
        <List
          items={[
            ['Microphone', 'voice channels and voice messages.'],
            ['Camera and gallery', 'take or pick a photo for a message, an avatar or a server icon.'],
            ['Notifications', 'messages and calls while the app is closed.'],
          ]}
        />
        <p className="mt-3">
          Each one is requested at the moment it is needed, and the app works without them — just
          without the matching feature.
        </p>
      </Section>

      <Section title="Changes and contact">
        <p>
          If the policy changes, a new date will appear here. Questions and requests to exercise your data rights —{' '}
          <a className="text-accent underline" href="mailto:privacy@cispracs.ru">
            privacy@cispracs.ru
          </a>
          .
        </p>
      </Section>
    </LegalDoc>
  );
}
